/**
 * SubscriptionManagement
 * Manage provider subscription tier, compare plans, view invoices, upgrade/downgrade.
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

// ── Tier definitions (mirroring ManageSubscription.js) ──────────────────────

const TIERS = {
  free: {
    name: 'Free',
    price: '$0',
    priceCents: 0,
    period: 'forever',
    features: [
      { text: 'Basic listing', included: true },
      { text: 'Limited analytics', included: true },
      { text: 'Featured badge', included: false },
      { text: 'Full analytics', included: false },
      { text: 'Lead notifications', included: false },
      { text: 'Priority support', included: false },
      { text: 'API access', included: false },
      { text: 'White-label reports', included: false },
      { text: 'Dedicated account manager', included: false },
    ],
  },
  pro: {
    name: 'Pro',
    price: '$49',
    priceCents: 4900,
    period: '/month',
    features: [
      { text: 'Basic listing', included: true },
      { text: 'Limited analytics', included: true },
      { text: 'Featured badge', included: true },
      { text: 'Full analytics', included: true },
      { text: 'Lead notifications', included: true },
      { text: 'Priority support', included: true },
      { text: 'API access', included: false },
      { text: 'White-label reports', included: false },
      { text: 'Dedicated account manager', included: false },
    ],
  },
  enterprise: {
    name: 'Enterprise',
    price: '$199',
    priceCents: 19900,
    period: '/month',
    features: [
      { text: 'Basic listing', included: true },
      { text: 'Limited analytics', included: true },
      { text: 'Featured badge', included: true },
      { text: 'Full analytics', included: true },
      { text: 'Lead notifications', included: true },
      { text: 'Priority support', included: true },
      { text: 'API access', included: true },
      { text: 'White-label reports', included: true },
      { text: 'Dedicated account manager', included: true },
    ],
  },
};

const TIER_ORDER = ['free', 'pro', 'enterprise'];

function generateMockInvoices() {
  const invoices = [];
  for (let i = 0; i < 10; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    invoices.push({
      id: `inv-${i + 1}`,
      date: d.toISOString(),
      amount: i < 3 ? 4900 : 4900,
      currency: 'CAD',
      status: i === 0 ? 'pending' : 'paid',
      description: 'Pro Plan - Monthly',
    });
  }
  return invoices;
}

export default function SubscriptionManagement() {
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch current subscription
  const { data: subscription } = useQuery({
    queryKey: ['provider-subscription'],
    queryFn: () => api.get('/api/provider-portal/subscription/tier').catch(() => null),
    placeholderData: null,
  });

  // Fetch invoices
  const { data: invoicesRaw } = useQuery({
    queryKey: ['provider-invoices'],
    queryFn: () => api.get('/api/provider-portal/subscription/invoices').catch(() => null),
    placeholderData: null,
  });

  const currentTier = subscription?.tier ?? 'free';
  const periodEnd = subscription?.currentPeriodEnd ?? null;
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd ?? false;
  const invoices = invoicesRaw?.invoices ?? generateMockInvoices();

  // Mutations
  const upgradeMutation = useMutation({
    mutationFn: (tier) =>
      api.post('/api/provider-portal/subscription/upgrade', {
        newTier: tier,
        successUrl: `${window.location.origin}/subscription?success=true`,
        cancelUrl: `${window.location.origin}/subscription`,
      }),
    onSuccess: (data) => {
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        queryClient.invalidateQueries({ queryKey: ['provider-subscription'] });
      }
    },
  });

  const downgradeMutation = useMutation({
    mutationFn: (tier) =>
      api.post('/api/provider-portal/subscription/downgrade', { newTier: tier }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-subscription'] }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/api/provider-portal/subscription/cancel'),
    onSuccess: () => {
      setShowCancelModal(false);
      queryClient.invalidateQueries({ queryKey: ['provider-subscription'] });
    },
  });

  const handleTierAction = useCallback((tierKey) => {
    const currentRank = TIER_ORDER.indexOf(currentTier);
    const targetRank = TIER_ORDER.indexOf(tierKey);
    if (targetRank > currentRank) {
      upgradeMutation.mutate(tierKey);
    } else if (targetRank < currentRank) {
      downgradeMutation.mutate(tierKey);
    }
  }, [currentTier, upgradeMutation, downgradeMutation]);

  const tierConfig = TIERS[currentTier] || TIERS.free;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
          Subscription Management
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Manage your plan, billing, and payment history.
        </p>
      </div>

      {/* Current plan card */}
      <div style={{
        padding: '24px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        marginBottom: '28px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Current Plan</h2>
          <StatusBadge status={currentTier} />
          {cancelAtPeriodEnd && (
            <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>
              Cancelling at period end
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', fontSize: '14px' }}>
          <div>
            <span style={{ color: '#64748b' }}>Price: </span>
            <strong>{tierConfig.price}{tierConfig.period !== 'forever' ? tierConfig.period : ''} CAD</strong>
          </div>
          {periodEnd && (
            <div>
              <span style={{ color: '#64748b' }}>Period ends: </span>
              <strong>{new Date(periodEnd).toLocaleDateString('en-CA')}</strong>
            </div>
          )}
          <div>
            <span style={{ color: '#64748b' }}>Features: </span>
            <strong>{tierConfig.features.filter((f) => f.included).length} included</strong>
          </div>
        </div>
        {currentTier !== 'free' && !cancelAtPeriodEnd && (
          <button
            type="button"
            onClick={() => setShowCancelModal(true)}
            style={{
              marginTop: '16px', padding: '8px 16px', fontSize: '13px', fontWeight: '600',
              borderRadius: '8px', border: '1px solid #fecaca', backgroundColor: '#fff',
              color: '#dc2626', cursor: 'pointer',
            }}
          >
            Cancel Subscription
          </button>
        )}
      </div>

      {/* Tier comparison */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px', marginBottom: '28px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '20px' }}>
          Compare Plans
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '600px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#64748b', fontWeight: '600' }}>Feature</th>
                {TIER_ORDER.map((key) => (
                  <th key={key} style={{
                    textAlign: 'center', padding: '10px 12px', fontWeight: '700',
                    color: key === currentTier ? '#3b82f6' : '#1e293b',
                    backgroundColor: key === currentTier ? '#eff6ff' : 'transparent',
                  }}>
                    {TIERS[key].name}
                    <div style={{ fontSize: '11px', fontWeight: '400', color: '#64748b', marginTop: '2px' }}>
                      {TIERS[key].price}{TIERS[key].period !== 'forever' ? TIERS[key].period : ''}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TIERS.enterprise.features.map((feature, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', color: '#475569' }}>{feature.text}</td>
                  {TIER_ORDER.map((key) => {
                    const included = TIERS[key].features[idx]?.included;
                    return (
                      <td key={key} style={{
                        textAlign: 'center', padding: '10px 12px',
                        backgroundColor: key === currentTier ? '#eff6ff' : 'transparent',
                      }}>
                        {included ? (
                          <span style={{ color: '#16a34a', fontSize: '16px', fontWeight: '700' }}>&#10003;</span>
                        ) : (
                          <span style={{ color: '#d1d5db', fontSize: '16px' }}>&mdash;</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Action row */}
              <tr>
                <td style={{ padding: '16px 12px' }} />
                {TIER_ORDER.map((key) => {
                  const isCurrentTier = key === currentTier;
                  const rank = TIER_ORDER.indexOf(key);
                  const currentRank = TIER_ORDER.indexOf(currentTier);
                  const isUpgrade = rank > currentRank;
                  const isDowngrade = rank < currentRank;
                  return (
                    <td key={key} style={{ textAlign: 'center', padding: '16px 12px' }}>
                      {isCurrentTier ? (
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#3b82f6' }}>Current Plan</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleTierAction(key)}
                          disabled={upgradeMutation.isPending || downgradeMutation.isPending}
                          style={{
                            padding: '8px 20px', fontSize: '13px', fontWeight: '600', borderRadius: '8px',
                            border: isUpgrade ? 'none' : '1px solid #d1d5db',
                            backgroundColor: isUpgrade ? '#3b82f6' : '#fff',
                            color: isUpgrade ? '#fff' : '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          {isUpgrade ? 'Upgrade' : 'Downgrade'}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Usage stats (API plan) */}
      {currentTier === 'enterprise' && (
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
          padding: '24px', marginBottom: '28px',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
            API Usage
          </h2>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Requests Today</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>1,247</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Daily Limit</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>50,000</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Usage</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>2.5%</div>
            </div>
          </div>
        </div>
      )}

      {/* Payment history */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
          Payment History
        </h2>
        {invoices.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>No invoices yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                {['Date', 'Description', 'Amount', 'Status'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#64748b', fontWeight: '600' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, 10).map((inv) => (
                <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', color: '#475569' }}>
                    {new Date(inv.date).toLocaleDateString('en-CA')}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#1e293b' }}>{inv.description}</td>
                  <td style={{ padding: '10px 12px', fontWeight: '600', color: '#1e293b' }}>
                    ${(inv.amount / 100).toFixed(2)} CAD
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <StatusBadge status={inv.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
            maxWidth: '440px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Cancel Subscription?
            </h3>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '24px' }}>
              Your subscription will remain active until the end of your current billing period.
              After that, you will be downgraded to the Free plan and lose access to premium features.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                style={{
                  padding: '10px 20px', fontSize: '14px', fontWeight: '600', borderRadius: '8px',
                  border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
                }}
              >
                Keep Plan
              </button>
              <button
                type="button"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                style={{
                  padding: '10px 20px', fontSize: '14px', fontWeight: '600', borderRadius: '8px',
                  border: 'none', backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer',
                }}
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
